// A project shown on the real screens without being in the device store: the
// tour's demo project. Home lists it and the plan screen opens it through
// this context, which only the tour provides, so outside the tour nothing
// here exists (and inside it, home leaves the setup list out). It lives in the tour's own memory: a tick or a snag on it
// comes back here and never reaches the store, the outbox or the server, so
// it never syncs and never moves into an account at the sign-up wall; it is
// gone when the tour unmounts. Tiny on purpose: home is in the shell.
import { createContext, useContext, type ReactNode } from 'react';
import type { Id, Project } from '@shared/plan';

/** What a snag on a stand-in says: the line, and the words. */
export interface StandInSnag { list: 'steps' | 'equipment'; itemId: Id; note: string }

export interface StandIn {
  /** The tour's project, once the tour has one to show; null on the beats before Generate and after the last. */
  project: Project | null;
  /** A tick, kept here, never in the store or its outbox. */
  tick(list: 'steps' | 'equipment', itemId: Id, done: boolean): void;
  /** A snag sent here, never to the server. */
  snag(draft: StandInSnag): void;
  /** Rows whose snag is on its way. */
  working: ReadonlySet<Id>;
}

export const StandInContext = createContext<StandIn | null>(null);

/** The tour's context inside the tour; null everywhere else. */
export const useStandIn = (): StandIn | null => useContext(StandInContext);

/**
 * The home screen, handed over by the shell (HomeRoute registers itself) for
 * the tour's home beats. The tour's chunk does not import it: a lazy chunk
 * importing the home screen's module makes the bundler split the shell into
 * a dozen small chunks, several kB more on every first load.
 */
let home: () => ReactNode = () => null;
export const homeScreen = {
  set(draw: () => ReactNode): void { home = draw; },
  draw: (): ReactNode => home(),
};
